/**
 * VisualSurface — shared chrome for graph and grid canvases.
 *
 * Provides consistent header, scroll body, optional zoom toolbar, legend slot,
 * and standard loading / empty / error states. On phone viewports, an optional
 * `fallback` replaces the canvas child (list-first UX for dense visuals).
 */

import { type ReactNode } from 'react';
import { Loader2, ZoomIn, ZoomOut, Maximize2, AlertCircle } from 'lucide-react';
import { useVisualViewportFlags } from './useVisualViewport';

export interface VisualSurfaceProps {
  /** Primary heading in the surface header. */
  title?: string;
  /** Secondary line under the title. */
  subtitle?: string;
  /** Icon shown beside the title. */
  icon?: ReactNode;
  /** Right-side header actions (filters, links, etc.). */
  headerActions?: ReactNode;
  /** Canvas content — SVG, React Flow, grid, etc. */
  children: ReactNode;
  /** Phone-only replacement when a list/read-only view is preferable. */
  fallback?: ReactNode;
  /** Force phone fallback regardless of viewport (testing / overrides). */
  forceFallback?: boolean;
  legend?: ReactNode;
  loading?: boolean;
  loadingLabel?: string;
  error?: string | null;
  /** Shown when `isEmpty` is true. */
  empty?: ReactNode;
  isEmpty?: boolean;
  className?: string;
  bodyClassName?: string;
  /** Controlled zoom (enables toolbar when `onZoomChange` is set). */
  zoom?: number;
  minZoom?: number;
  maxZoom?: number;
  onZoomChange?: (zoom: number) => void;
  onFitView?: () => void;
  /** Show +/- / fit controls. Defaults to true when `onZoomChange` is provided. */
  showZoomControls?: boolean;
}

const DEFAULT_MIN_ZOOM = 0.4;
const DEFAULT_MAX_ZOOM = 1.5;
const ZOOM_STEP = 0.1;

export function VisualSurface({
  title,
  subtitle,
  icon,
  headerActions,
  children,
  fallback,
  forceFallback = false,
  legend,
  loading = false,
  loadingLabel = 'Loading…',
  error = null,
  empty,
  isEmpty = false,
  className = '',
  bodyClassName = '',
  zoom,
  minZoom = DEFAULT_MIN_ZOOM,
  maxZoom = DEFAULT_MAX_ZOOM,
  onZoomChange,
  onFitView,
  showZoomControls,
}: VisualSurfaceProps) {
  const { isPhone } = useVisualViewportFlags();
  const useFallback = (forceFallback || isPhone) && fallback != null;
  const zoomEnabled = showZoomControls ?? onZoomChange != null;
  const zoomValue = zoom ?? 1;

  const hasHeader = title != null || icon != null || headerActions != null;

  if (loading) {
    return (
      <div className={` border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] ${className}`}>
        <div className="flex items-center justify-center gap-2 py-12 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">{loadingLabel}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={` border border-[var(--coral)] dark:border-[var(--coral)]/40 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] ${className}`}>
        <div className="flex items-start gap-2 px-4 py-6 text-[var(--coral)] dark:text-[var(--coral)]">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (isEmpty && empty) {
    return (
      <div className={` border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] ${className}`}>
        {empty}
      </div>
    );
  }

  return (
    <div className={` border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden ${className}`}>
      {hasHeader && (
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <div className="flex items-center gap-2 min-w-0">
            {icon && <span className="flex-shrink-0">{icon}</span>}
            {(title || subtitle) && (
              <div className="min-w-0">
                {title && (
                  <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                    {title}
                  </p>
                )}
                {subtitle && (
                  <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] truncate">{subtitle}</p>
                )}
              </div>
            )}
          </div>
          {headerActions && <div className="flex-shrink-0">{headerActions}</div>}
        </div>
      )}

      {zoomEnabled && onZoomChange && (
        <div className="flex items-center justify-end gap-1 px-3 py-1.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)]/80 dark:bg-[color-mix(in_srgb,var(--surface)_50%,transparent)]">
          <button
            type="button"
            onClick={() => onZoomChange(Math.max(minZoom, Math.round((zoomValue - ZOOM_STEP) * 10) / 10))}
            disabled={zoomValue <= minZoom}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-pri)] hover:bg-[var(--bg-raised)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-pri)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-40 disabled:pointer-events-none transition-colors"
            title="Zoom out"
            aria-label="Zoom out"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <span className="text-xs font-mono text-[var(--text-ter)] dark:text-[var(--text-dim)] tabular-nums min-w-[3rem] text-center">
            {Math.round(zoomValue * 100)}%
          </span>
          <button
            type="button"
            onClick={() => onZoomChange(Math.min(maxZoom, Math.round((zoomValue + ZOOM_STEP) * 10) / 10))}
            disabled={zoomValue >= maxZoom}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-pri)] hover:bg-[var(--bg-raised)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-pri)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-40 disabled:pointer-events-none transition-colors"
            title="Zoom in"
            aria-label="Zoom in"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
          {onFitView && (
            <button
              type="button"
              onClick={onFitView}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-pri)] hover:bg-[var(--bg-raised)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-pri)] dark:hover:bg-[var(--hover-tint)] transition-colors ml-1"
              title="Fit to view"
              aria-label="Fit to view"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      <div className={`overflow-auto ${bodyClassName}`}>
        {useFallback ? fallback : children}
      </div>

      {legend && (
        <div className="px-4 pb-3 pt-2 border-t border-[var(--rule)] dark:border-[var(--rule)]">
          {legend}
        </div>
      )}
    </div>
  );
}
